import React, { useEffect } from 'react';
import { Flag, Award } from 'lucide-react';

interface HistoryItemProps {
  year: string;
  events: string[];
  isLast?: boolean;
}

const HistoryItem: React.FC<HistoryItemProps> = ({ year, events, isLast }) => (
  <div className="flex group">
    {/* Left Column: Year */}
    <div className="w-24 md:w-32 flex-shrink-0 flex flex-col items-center">
      <div className="text-2xl md:text-3xl font-display font-bold text-primary group-hover:text-secondary transition-colors duration-300">
        {year}
      </div>
    </div>

    {/* Center Column: Timeline Line & Dot */}
    <div className="flex flex-col items-center mx-4 relative">
      <div className="w-4 h-4 rounded-full bg-slate-300 border-4 border-white shadow-sm z-10 group-hover:bg-accent group-hover:scale-125 transition-all duration-300"></div>
      {!isLast && <div className="flex-grow w-0.5 bg-slate-200 mt-2"></div>}
    </div>

    {/* Right Column: Events */}
    <div className="flex-grow pb-12 pt-[-4px]">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-300 relative -top-3">
        <ul className="space-y-3">
          {events.map((event, index) => (
            <li key={index} className="text-slate-600 text-lg leading-relaxed flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 mr-3 flex-shrink-0"></span>
              {event}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const History = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const historyData = [
    {
      year: "2022",
      events: ["06 연구개발전담부서 설립"]
    },
    {
      year: "2021",
      events: ["10 통합 PC 보안 솔루션 LDP 구축", "10 천안시 기업인상 수상"]
    },
    {
      year: "2018",
      events: ["08 티브로드 방송 송출 “우리들의 일터”"]
    },
    {
      year: "2017",
      events: ["12 충청남도 우수기업상 수상"]
    },
    {
      year: "2015",
      events: ["12 SEMES 품질 시스템 S.S.Q. 우수협력사 선정", "07 사업장 확장 이전 (백석공단1로 70)"]
    },
    {
      year: "2014",
      events: ["12 특례업체 선정"]
    },
    {
      year: "2012",
      events: ["03 S.S 기업(SEMES) 2차 VENDOR 승인"]
    },
    {
      year: "2011",
      events: ["01 벤처기업 인증"]
    },
    {
      year: "2010",
      events: ["07 법인전환 (파워테크 → 파워테크㈜)", "06 사업장 확장 이전 (백석공단 5길 6)"]
    },
    {
      year: "2009",
      events: ["01 반도체 IRIS, K-SPIN C-BOX 생산"]
    },
    {
      year: "2008",
      events: ["08 LCD LINE HARNESS 생산"]
    },
    {
      year: "2007",
      events: ["05 반도체 장비 자동 제어기 생산"]
    },
    {
      year: "2006",
      events: ["08 반도체 (IRIS, K-SPIN, LOZIX) HARNESS 생산", "05 삼성 ROBOT 제어기 C-BOX, HARNESS 생산", "05 ㈜METIS 협력업체 등록"]
    },
    {
      year: "2004",
      events: ["01 영보엔지니어링㈜ 품질우수업체 선정"]
    },
    {
      year: "2003",
      events: ["03 영보엔지니어링㈜ 시범업체 선정"]
    },
    {
      year: "2002",
      events: [
        "09 삼성 휴대폰 Battery Sub Ass’y 생산",
        "08 영보엔지니어링㈜ 협력업체 등록",
        "08 파워테크 설립"
      ]
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Our History</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">파워테크 연혁</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            끊임없는 도전과 혁신으로 걸어온 파워테크의 발자취를 소개합니다.
          </p>
        </div>
      </section>

      {/* Intro Quote */}
      <section className="py-20 bg-slate-50">
        <div className="section-container text-center">
          <Award className="w-12 h-12 text-accent mx-auto mb-6" />
          <p className="text-2xl md:text-3xl font-display font-bold text-slate-800 leading-normal max-w-4xl mx-auto">
            "파워테크는 지난 20여 년간 <span className="text-primary">신뢰와 기술</span>을 바탕으로<br className="hidden md:block" /> 성장해왔으며, 더 큰 도약을 준비하고 있습니다."
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-white relative">
        <div className="section-container max-w-4xl">
          {/* Year Groups */}
          <div className="relative">
            {/* Vertical Line Background (Desktop) - Optional visual aid */}
            {/* <div className="absolute left-[8.5rem] top-0 bottom-0 w-0.5 bg-slate-100 hidden md:block"></div> */}

            {historyData.map((item, index) => (
              <HistoryItem
                key={index}
                year={item.year}
                events={item.events}
                isLast={index === historyData.length - 1}
              />
            ))}
          </div>

          <div className="mt-20 flex justify-center">
            <div className="bg-slate-900 text-white px-8 py-6 rounded-2xl flex items-center shadow-xl">
              <Flag className="text-accent mr-4 flex-shrink-0" size={32} />
              <div>
                <div className="font-bold text-lg mb-1">새로운 미래를 향해</div>
                <div className="text-slate-400 text-sm">파워테크는 오늘도 멈추지 않고 나아갑니다.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default History;
